import type { PropsWithChildren } from 'react';

import { ExLeaseBadge } from 'Components/shared/ExLeaseBadge';
import { MobilizeBadge } from 'Components/shared/MobilizeBadge';
import { GalleryDataProvider } from 'Context/GalleryDataContext/GalleryDataContext';
import { useVehicleData } from 'Context/VehicleContext/VehicleDataContext';
import {
	triggerGalleryClosedEvent,
	triggerGalleryDamageLocationClickedEvent,
	triggerGalleryImageViewedEvent,
	triggerGalleryNextClickedEvent,
	triggerGalleryPrevClickedEvent,
	triggerGalleryTabClickedEvent,
	triggerGalleryThumbClickedEvent,
	triggerGalleryZoomInClickedEvent,
	triggerGalleryZoomOutClickedEvent,
	triggerGalleryZoomResetClickedEvent,
} from 'Services/analytics/events/galleryEventsRedesign';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';

import { GalleryProvider } from './Components/GalleryProvider';

export const VehicleDetailsRedesignGallery: React.FC<PropsWithChildren> = ({ children }) => {
	const vehicle = useVehicleData();
	const isExLease = useFeatureToggle(FEATURES.exLeaseBranding);
	const {
		gallery: { bodyType, categories, images },
	} = vehicle;
	const isMobilizeVehicle = vehicle.sourcePartner?.toLowerCase() === 'mobilize';
	const isExLeaseVehicle = isExLease && vehicle.seller?.isTrade && !isMobilizeVehicle;

	const getOverlayBadge = () => {
		if (isMobilizeVehicle) return <MobilizeBadge size="large" />;
		if (isExLeaseVehicle) return <ExLeaseBadge size="large" />;
		return undefined;
	};

	return (
		<GalleryDataProvider bodyType={bodyType} categories={categories} images={images}>
			<GalleryProvider
				events={{
					onClose: triggerGalleryClosedEvent,
					onDamageLocationView: triggerGalleryDamageLocationClickedEvent,
					onNext: triggerGalleryNextClickedEvent,
					onPrev: triggerGalleryPrevClickedEvent,
					onSlideView: triggerGalleryImageViewedEvent,
					onTabClick: triggerGalleryTabClickedEvent,
					onThumbClick: triggerGalleryThumbClickedEvent,
					onZoomIn: triggerGalleryZoomInClickedEvent,
					onZoomOut: triggerGalleryZoomOutClickedEvent,
					onZoomReset: triggerGalleryZoomResetClickedEvent,
				}}
				overlayBadge={getOverlayBadge()}
			>
				{children}
			</GalleryProvider>
		</GalleryDataProvider>
	);
};
